import { GeneralTaskChildrenTabQuery$data } from "./tabs/__generated__/GeneralTaskChildrenTabQuery.graphql";
import {
  ValidatedChildren,
  ClipBoardObject,
  FilteredArguments,
  GeneralTaskKeyValueListPairs,
  GeneralTaskDetails,
  SweepArguments,
} from "./generalTask.types";
import { GeneralTaskPageQuery$data } from "./__generated__/GeneralTaskPageQuery.graphql";

export const maxLength = parseInt(import.meta.env.VITE_REPORTS_LIMIT ?? "24");

export const sweepsList = (
  arg_lists: GeneralTaskKeyValueListPairs,
  sweeps: readonly (string | null | undefined)[],
): GeneralTaskKeyValueListPairs => {
  if (arg_lists) return arg_lists.filter((el) => sweeps.includes(el ? el.k : ""));
  return [];
};

export const updateFilteredArguments = (
  currentFilteredArguments: FilteredArguments,
  newValue: string[],
  name: string,
): FilteredArguments => {
  const filteredArguments = [...currentFilteredArguments.data];
  const itemIndex = filteredArguments.findIndex((item) => item.k === name);

  if (itemIndex !== -1) {
    filteredArguments[itemIndex].v = newValue;
    if (filteredArguments[itemIndex].v.length === 0) {
      filteredArguments.splice(itemIndex, 1);
    }
  } else {
    filteredArguments.push({
      k: name,
      v: newValue,
    });
  }

  return {
    data: filteredArguments,
  };
};

export const validateChildTasks = (data: GeneralTaskChildrenTabQuery$data): ValidatedChildren => {
  const childTasks = data?.node?.children?.edges?.map((e) => e?.node?.child);
  const validatedChildTasks: ValidatedChildren = { data: [] };
  childTasks?.map((task) => {
    if (
      task &&
      task.__typename !== "%other" &&
      task.id &&
      task.duration &&
      task.state &&
      task.result &&
      task.arguments //&&
      //task.inversion_solution
    ) {
      const ruptureSetFile =
        task.__typename === "RuptureGenerationTask"
          ? task.files?.edges?.map((e) => e?.node?.file).find((file) => file?.__typename === "RuptureSet")
          : undefined;

      validatedChildTasks.data?.push({
        __typename: task.__typename,
        id: task.id,
        created: task.created,
        duration: task.duration,
        state: task.state,
        result: task.result,
        arguments: task.arguments,
        inversion_solution: task.inversion_solution ?? undefined,
        rupture_set: ruptureSetFile?.id ? { id: ruptureSetFile.id, meta: ruptureSetFile.meta } : undefined,
      });
    }
  });

  return validatedChildTasks;
};

export const applyChildTaskFilter = (
  childTasks: ValidatedChildren,
  filteredArguments: FilteredArguments,
): ValidatedChildren => {
  const filtered = childTasks.data?.filter((child) => {
    return filteredArguments.data?.every((sweepArgument) => {
      return child?.arguments?.some((argument) => {
        return (
          (sweepArgument.k.includes(argument?.k as string) || pluralCompare(sweepArgument.k, argument?.k as string)) &&
          sweepArgument.v.includes(argument?.v as string)
        );
      });
    });
  });
  return { data: filtered };
};

/**
 * Before we want to show a swept argument as a filter to the user, we need to make sure that each child actually
 * has this as an argument.
 * @param sweepArgument
 * @param childTasks
 * @returns
 */
export const tidySweptArguments = (
  sweepArgument: SweepArguments | undefined,
  childTasks: ValidatedChildren,
): SweepArguments | undefined => {
  const cleaned = sweepArgument?.filter((sweepArgument) => {
    return (
      sweepArgument &&
      childTasks.data?.every((child) => {
        return child?.arguments.some((argument) => {
          return (
            sweepArgument.k &&
            (sweepArgument.k.includes(argument?.k as string) || pluralCompare(sweepArgument.k, argument?.k as string))
          );
        });
      })
    );
  });
  if (cleaned?.length == 0) {
    return undefined;
  }
  return cleaned;
};

export const getGeneralTaskDetailsFromQueryResponse = (data: GeneralTaskPageQuery$data): GeneralTaskDetails => {
  return {
    __typename: "GeneralTask",
    title: data?.node?.title ?? "",
    id: data?.node?.id ?? "",
    created: (data?.node?.created as string) ?? "",
    model_type: data?.node?.model_type ?? null,
    description: data?.node?.description ?? "",
    notes: data?.node?.notes ?? "",
    swept_arguments: (data?.node?.swept_arguments as string[]) ?? [],
    argument_lists: data?.node?.argument_lists ?? [],
  };
};

export const getClipBoardObject = async (search: string): Promise<ClipBoardObject> => {
  const clipBoard = new URLSearchParams(search).get("clipBoard") ?? "";
  const clipBoardDecoded = atob(clipBoard);
  const clipBoardObject = JSON.parse(clipBoardDecoded);
  return clipBoardObject;
};

export const pluralCompare = (sweep: string, argName: string): boolean => {
  const pluralArgName = argName.replace("value", "values");
  return pluralArgName === sweep;
};

export const determineClipBoard = (search: string): boolean => {
  const clipBoardString = new URLSearchParams(search).get("clipBoard") ?? "";
  return clipBoardString.length > 0;
};

export const getChildTaskIdArray = (filteredChildren: ValidatedChildren): string[] => {
  const idArray: string[] = [];
  if (filteredChildren.data && filteredChildren.data.length <= maxLength) {
    filteredChildren.data?.map((task) => {
      idArray.push(task?.inversion_solution?.id || "");
    });
    return idArray;
  } else {
    return [];
  }
};

export const getRuptureSetIdArray = (filteredChildren: ValidatedChildren): string[] => {
  const idArray: string[] = [];
  if (filteredChildren.data && filteredChildren.data.length <= maxLength) {
    filteredChildren.data?.map((task) => {
      idArray.push(task?.rupture_set?.id || "");
    });
    return idArray;
  } else {
    return [];
  }
};
